import { zodResolver } from '@hookform/resolvers/zod';
import { Tag } from 'baseui/tag';
import { Link2 } from 'react-feather';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { FormController } from 'components/FormController';
import { FieldGroup } from 'components/forms/fieldset';
import { QueryResolver } from 'components/QueryResolver';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from 'components/ui/dialog';
import { Input, TextField } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { CountrySelect } from 'features/CountrySelect';
import { StateSelect } from 'features/StateSelect';
import { useCustomerQuery } from 'services/api/customers/endpoints';
import { rawAddressSchema } from 'services/api/types.shared';
import { useLocale } from 'utils/hooks/useLocale';

const modifiableAddressSchema = rawAddressSchema.pick({
  street: true,
  street2: true,
  city: true,
  state: true,
  postalCode: true,
  country: true,
});

const schema = z.object({
  firstName: z.string().min(1, { message: 'First name is required' }),
  lastName: z.string().min(1, { message: 'Last name is required' }),
  businessName: z.string().optional(),
  email: z.string().email({ message: 'Invalid email address' }),
  phone: z.string().min(1, { message: 'Phone number is required' }),
  notes: z.string().optional(),
  address: modifiableAddressSchema,
  isCompany: z.boolean().optional(),
});

type FormFields = z.infer<typeof schema>;

export default function EditCustomerModal({
  customerId,
  isOpen,
  onClose,
}: {
  customerId: number;
  isOpen: boolean;
  onClose: () => void;
}) {
  const { t } = useLocale();
  const customerQuery = useCustomerQuery(customerId, { skip: !isOpen });

  return (
    <Dialog
      onOpenChange={(open) => !open && onClose()}
      defaultOpen={isOpen}
      size="2xl"
    >
      <DialogTitle className="mt-4">{t('Customer Details')}</DialogTitle>
      <DialogBody>
        <QueryResolver query={customerQuery}>
          {(customer) => {
            const isQuickBooksLinked = customer.quickbooksId !== null;

            return (
              <CustomerForm
                id="edit-customer-modal-form"
                initialValue={{
                  firstName: customer.firstName,
                  lastName: customer.lastName,
                  businessName: customer.businessName ?? '',
                  email: customer.email,
                  phone: customer.phone,
                  notes: customer.notes,
                  address: {
                    street: customer.address.street,
                    street2: customer.address.street2,
                    city: customer.address.city,
                    state: customer.address.state,
                    postalCode: customer.address.postalCode,
                    country: customer.address.country,
                  },
                  isCompany: customer.firstName === customer.businessName,
                }}
                isQuickBooksLinked={isQuickBooksLinked}
              />
            );
          }}
        </QueryResolver>
      </DialogBody>
      <DialogActions>
        <Button slot="close" size="lg" variant="plain">
          {t('Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CustomerForm({
  initialValue,
  id = '',
  isQuickBooksLinked,
}: {
  initialValue?: FormFields;
  id?: string;
  isQuickBooksLinked: boolean;
}) {
  const { t } = useLocale();
  const { control, watch } = useForm<FormFields>({
    defaultValues: {
      address: {
        country: '',
      },
      isCompany: false,
      ...(initialValue ?? {}),
    },
    resolver: zodResolver(schema),
  });

  const selectedCountryCode = watch('address.country');

  return (
    <form id={id}>
      <FieldGroup>
        <div className="flex justify-start">
          {isQuickBooksLinked ? (
            <Tag
              size="small"
              variant="light"
              kind="custom"
              closeable={false}
              color="#22c55e"
              overrides={{
                Root: {
                  style: {
                    marginLeft: '0',
                    marginRight: '0',
                    marginTop: '0',
                    marginBottom: '0',
                  },
                },
              }}
              startEnhancer={() => {
                return (
                  <>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 66 60"
                      xmlns="http://www.w3.org/2000/svg"
                      className="text-green-500"
                      fill="currentColor"
                      aria-label="QuickBooks Icon"
                    >
                      <path d="M.5 30c0 9.665 7.835 17.5 17.5 17.5h2.5V41H18c-6.075 0-11-4.925-11-11s4.925-11 11-11h6v34c0 1.724.685 3.377 1.904 4.596 1.22 1.22 2.872 1.904 4.596 1.904v-47H18C8.335 12.5.5 20.335.5 30zM48 12.5h-2.5V19H48c6.075 0 11 4.925 11 11s-4.925 11-11 11h-6V7C42 3.41 39.09.5 35.5.5v47H48c6.252 0 12.03-3.335 15.155-8.75 3.127-5.415 3.127-12.085 0-17.5C60.03 15.835 54.252 12.5 48 12.5z" />
                    </svg>
                  </>
                );
              }}
            >
              {t('Connected')}
            </Tag>
          ) : (
            <Tag
              size="small"
              variant="light"
              kind="custom"
              closeable={false}
              color="#9E9E9E"
              overrides={{
                Root: {
                  style: {
                    marginLeft: '0',
                    marginRight: '0',
                    marginTop: '0',
                    marginBottom: '0',
                  },
                },
              }}
              startEnhancer={() => {
                return (
                  <>
                    <Link2 height={16} width={16} strokeWidth={4} />
                  </>
                );
              }}
            >
              {t('Unlinked')}
            </Tag>
          )}
        </div>
        <div className="grid gap-x-4 gap-y-5 lg:grid-cols-2">
          <TextField control={control} field="firstName" isDisabled={true}>
            <Label>{t('First Name')}</Label>
            <Input />
          </TextField>
          <TextField control={control} field="lastName" isDisabled={true}>
            <Label>{t('Last Name')}</Label>
            <Input />
          </TextField>
        </div>

        <div className="grid gap-x-4 gap-y-5 lg:grid-cols-2">
          <TextField control={control} field="email" isDisabled={true}>
            <Label>{t('Email Address')}</Label>
            <Input type="email" />
          </TextField>
          <TextField control={control} field="phone" isDisabled={true}>
            <Label>{t('Phone Number')}</Label>
            <Input />
          </TextField>
        </div>

        <div className="grid gap-x-4 gap-y-5 lg:grid-cols-1">
          <TextField control={control} field="businessName" isDisabled={true}>
            <Label>{t('Company (Optional)')}</Label>
            <Input />
          </TextField>
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-semibold">{t('Address')}</h3>

          <div className="grid gap-x-4 gap-y-5 lg:grid-cols-2">
            <TextField
              control={control}
              field="address.street"
              isDisabled={true}
            >
              <Label>{t('Address')}</Label>
              <Input />
            </TextField>
            <TextField
              control={control}
              field="address.street2"
              isDisabled={true}
            >
              <Label>{t('Unit')}</Label>
              <Input />
            </TextField>
          </div>

          <div className="grid gap-x-4 gap-y-5 lg:grid-cols-2">
            <TextField control={control} field="address.city" isDisabled={true}>
              <Label>{t('City')}</Label>
              <Input />
            </TextField>
            <FormController
              control={control}
              label={t('Country')}
              name="address.country"
              defaultValue=""
            >
              {({ field }) => (
                <CountrySelect
                  value={field.value}
                  onChange={field.onChange}
                  disabled={true}
                  isCountryEmpty={true}
                  emptyPlaceholder="N/A"
                />
              )}
            </FormController>
          </div>

          <div className="grid gap-x-4 gap-y-5 lg:grid-cols-2">
            <TextField
              control={control}
              field="address.postalCode"
              isDisabled={true}
            >
              <Label>{t('Zip')}</Label>
              <Input />
            </TextField>
            <FormController
              control={control}
              label={t('State')}
              name="address.state"
              defaultValue=""
            >
              {({ field }) => (
                <StateSelect
                  useClientCountryQuery={true}
                  countryCode={selectedCountryCode}
                  value={field.value}
                  onChange={field.onChange}
                  disabled={true}
                  isStateEmpty={true}
                  emptyPlaceholder="N/A"
                />
              )}
            </FormController>
          </div>
        </div>
      </FieldGroup>
    </form>
  );
}
